import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clapperboard, Play, ExternalLink, X, ChevronLeft, ChevronRight } from "lucide-react";

function youtubeId(url) {
  const m = String(url).match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{6,})/);
  return m ? m[1] : null;
}

export default function MediaSection({ media }) {
  const [lightbox, setLightbox] = useState(null);

  const images = (media || []).filter((m) => m.type === "image");

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const stepLightbox = useCallback((dir) => {
    setLightbox((cur) => (cur == null ? cur : (cur + dir + images.length) % images.length));
  }, [images.length]);

  useEffect(() => {
    if (lightbox == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") stepLightbox(1);
      if (e.key === "ArrowLeft") stepLightbox(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, closeLightbox, stepLightbox]);

  if (!media || media.length === 0) return null;

  const videos = media.filter((m) => m.type === "youtube");
  const xClips = media.filter((m) => m.type === "x");

  return (
    <>
    <section data-testid="media-section" className="mt-4 border border-white/10 bg-black/40 backdrop-blur-md relative overflow-hidden">
      <div className="absolute inset-0 grid-texture opacity-40" />
      <div className="relative p-6 md:p-10">
        <div className="flex items-center gap-4 mb-8">
          <Clapperboard className="w-5 h-5 text-nc-blue" />
          <h3 data-testid="media-title" className="font-sans font-semibold uppercase tracking-[0.3em] text-lg md:text-xl text-zinc-100">
            Media // Field Recordings
          </h3>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {videos.map((m, i) => {
          const id = youtubeId(m.url);
          if (!id) return null;
          return (
            <motion.figure
              key={m.id}
              data-testid={`media-youtube-${i + 1}`}
              className="mb-8"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="border border-white/15 aspect-video bg-black">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube-nocookie.com/embed/${id}`}
                  title={m.caption || "New Citizen video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {m.caption && (
                <figcaption className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-3">{m.caption}</figcaption>
              )}
            </motion.figure>
          );
        })}

        {images.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {images.map((m, i) => (
              <motion.figure
                key={m.id}
                data-testid={`media-image-${i + 1}`}
                className="group"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <div className="border border-white/10 overflow-hidden group-hover:border-white/30 transition-colors duration-300 cursor-zoom-in" onClick={() => setLightbox(i)}>
                  <img
                    src={m.url}
                    alt={m.caption || "New Citizen screenshot"}
                    loading="lazy"
                    className="w-full aspect-video object-cover group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                {m.caption && (
                  <figcaption className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-2">{m.caption}</figcaption>
                )}
              </motion.figure>
            ))}
          </div>
        )}

        {xClips.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {xClips.map((m, i) => (
              <a
                key={m.id}
                data-testid={`media-x-${i + 1}`}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/10 bg-[#050505]/60 p-4 flex items-center gap-3 hover:border-nc-blue/50 hover:bg-nc-blue/5 transition-colors duration-300 group"
              >
                <span className="w-9 h-9 border border-nc-blue/40 bg-nc-blue/10 flex items-center justify-center shrink-0">
                  <Play className="w-4 h-4 text-nc-blue" />
                </span>
                <span className="font-mono text-[11px] tracking-[0.15em] text-zinc-300 uppercase flex-1">{m.caption || "Gameplay clip"}</span>
                <ExternalLink className="w-3.5 h-3.5 text-zinc-600 group-hover:text-nc-blue transition-colors duration-300 shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </section>

    <AnimatePresence>
      {lightbox != null && images[lightbox] && (
        <motion.div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeLightbox}
        >
          <button data-testid="lightbox-close" onClick={closeLightbox} className="absolute top-4 right-4 w-10 h-10 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-white hover:border-white/50 transition-colors duration-300">
            <X className="w-5 h-5" />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); stepLightbox(-1); }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-nc-blue hover:border-nc-blue/60 transition-colors duration-300"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); stepLightbox(1); }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-nc-blue hover:border-nc-blue/60 transition-colors duration-300"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <motion.img
              key={images[lightbox].url}
              data-testid="lightbox-image"
              src={images[lightbox].url}
              alt={images[lightbox].caption || "Screenshot"}
              className="w-full max-h-[80vh] object-contain border border-white/15"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.25 }}
            />
            <figcaption className="flex items-center justify-between mt-3">
              <span data-testid="lightbox-caption" className="font-mono text-[10px] tracking-[0.25em] text-zinc-400 uppercase">
                {images[lightbox].caption}
              </span>
              <span className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">
                {lightbox + 1} / {images.length}
              </span>
            </figcaption>
          </figure>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
}
